import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiConfigService } from './api-config.service';
import { ApiResponse, QueryParams, ErrorResponseModel } from '../interfaces/base.interface';

/**
 * Base API Service with common HTTP operations
 */
@Injectable({
    providedIn: 'root'
})
export class BaseApiService {
    constructor(
        protected http: HttpClient,
        protected apiConfig: ApiConfigService
    ) { }

    /**
     * GET request
     */
    protected get<T>(endpoint: string, params?: QueryParams): Observable<ApiResponse<T>> {
        const url = this.apiConfig.getApiUrl(endpoint);
        const httpParams = this.buildHttpParams(params);

        return this.http.get<T>(url, { params: httpParams })
            .pipe(
                map(response => this.handleSuccess<T>(response)),
                catchError(error => this.handleError<T>(error))
            );
    }

    /**
     * POST request
     */
    protected post<T>(endpoint: string, body?: any): Observable<ApiResponse<T>> {
        const url = this.apiConfig.getApiUrl(endpoint);

        return this.http.post<T>(url, body, {
            headers: this.apiConfig.getDefaultHeaders()
        })
            .pipe(
                map(response => this.handleSuccess<T>(response)),
                catchError(error => this.handleError<T>(error))
            );
    }

    /**
     * PUT request
     */
    protected put<T>(endpoint: string, body?: any): Observable<ApiResponse<T>> {
        const url = this.apiConfig.getApiUrl(endpoint);

        return this.http.put<T>(url, body, {
            headers: this.apiConfig.getDefaultHeaders()
        })
            .pipe(
                map(response => this.handleSuccess<T>(response)),
                catchError(error => this.handleError<T>(error))
            );
    }

    /**
     * DELETE request
     */
    protected delete<T>(endpoint: string): Observable<ApiResponse<T>> {
        const url = this.apiConfig.getApiUrl(endpoint);

        return this.http.delete<T>(url)
            .pipe(
                map(response => this.handleSuccess<T>(response)),
                catchError(error => this.handleError<T>(error))
            );
    }

    /**
     * Build HttpParams from QueryParams
     */
    private buildHttpParams(params?: QueryParams): HttpParams {
        let httpParams = new HttpParams();

        if (params) {
            Object.keys(params).forEach(key => {
                const value = params[key];
                if (value !== null && value !== undefined) {
                    httpParams = httpParams.set(key, value.toString());
                }
            });
        }

        return httpParams;
    }

    /**
     * Handle successful response
     */
    private handleSuccess<T>(response: T): ApiResponse<T> {
        return {
            success: true,
            data: response
        };
    }

    /**
     * Handle error response
     */
    private handleError<T>(error: HttpErrorResponse): Observable<ApiResponse<T>> {
        let errorResponse: ApiResponse<T> = {
            success: false,
            error: {
                Error: {
                    Code: error.status.toString(),
                    Message: error.message || 'An unexpected error occurred',
                    Details: [],
                    Timestamp: new Date()
                }
            }
        };

        if (error.error) {
            try {
                const apiError = error.error as ErrorResponseModel;
                errorResponse.error = apiError;
            } catch (e) {
                // If parsing fails, use the default error structure
            }
        }

        return throwError(() => errorResponse);
    }

    /**
 * GET request for Blob (file download)
 */
protected getBlob(endpoint: string): Observable<Blob> {
    const url = this.apiConfig.getApiUrl(endpoint);

    return this.http.get(url, {
        responseType: 'blob'
    });
}
}
